import React, { useEffect, useMemo, useState } from "react";
import {
  Trophy,
  Gamepad2,
  Activity,
  RefreshCw,
  Swords,
  Skull,
  Target,
  Zap,
  Play,
} from "lucide-react";

import {
  fetchPlayZoneLeaderboards,
  fetchPlayZoneActivity,
} from "@/lib/api";

const number = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0
    ? n.toLocaleString("en-IN")
    : "0";
};

const clean = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const timeAgo = (date) => {
  if (!date) return "";

  const diff = Math.max(
    0,
    Date.now() - new Date(date).getTime()
  );

  const seconds = Math.floor(diff / 1000);

  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const MODE_STAT_LABELS = {
  wins: {
    label: "Wins",
    icon: Trophy,
  },
  played: {
    label: "Played",
    icon: Play,
  },
  kills: {
    label: "Kills",
    icon: Swords,
  },
  deaths: {
    label: "Deaths",
    icon: Skull,
  },
  final_kills: {
    label: "Final Kills",
    icon: Swords,
  },
  hider_wins: {
    label: "Hider Wins",
    icon: Trophy,
  },
  seeker_wins: {
    label: "Seeker Wins",
    icon: Target,
  },
  attempts: {
    label: "Attempts",
    icon: Play,
  },
  eggs_fired: {
    label: "Eggs Fired",
    icon: Target,
  },
  blocks_hit: {
    label: "Blocks Hit",
    icon: Target,
  },
  arrows_hit: {
    label: "Arrows Hit",
    icon: Target,
  },
  arrows_shot: {
    label: "Arrows Shot",
    icon: Target,
  },
  wither_kills: {
    label: "Wither Kills",
    icon: Skull,
  },
  wither_damage: {
    label: "Wither Damage",
    icon: Zap,
  },
};

const OVERALL_STATS = [
  "wins",
  "played",
  "kills",
  "deaths",
];

const MODE_STAT_ORDER = [
  "wins",
  "played",
  "kills",
  "final_kills",
  "deaths",
  "hider_wins",
  "seeker_wins",
  "attempts",
  "eggs_fired",
  "blocks_hit",
  "arrows_hit",
  "arrows_shot",
  "wither_kills",
  "wither_damage",
];

function Stat({ stat, value, compact = false }) {
  const config = MODE_STAT_LABELS[stat];

  if (!config) return null;

  const Icon = config.icon;

  return (
    <div
      className={`flex items-center gap-1.5 ${
        compact ? "text-xs" : "text-xs"
      }`}
    >
      <Icon
        size={compact ? 11 : 12}
        className="text-white/35 shrink-0"
      />

      <span className="text-white/35">
        {config.label}
      </span>

      <span className="font-black text-sm sm:text-base text-white/90">
        {number(clean(value))}
      </span>
    </div>
  );
}

function PlayerAvatar({ player }) {
  return (
    <img
      src={`https://mc-heads.net/avatar/${encodeURIComponent(
        player.name
      )}/64`}
      alt={player.name}
      className="w-11 h-11 rounded-xl border border-white/10 bg-black/40"
      loading="lazy"
    />
  );
}

function RankBadge({ rank }) {
  const color =
    rank === 1
      ? "text-[#FFBF00]"
      : rank === 2
      ? "text-slate-300"
      : "text-orange-400";

  return (
    <div
      className={`w-8 text-center font-black text-sm ${color}`}
    >
      #{rank}
    </div>
  );
}

function PlayerCard({ player, overall = false, modeStats = [] }) {
  const played = clean(player.played);
  const wins = clean(player.wins);

  const winRate =
    played > 0
      ? ((wins / played) * 100).toFixed(1)
      : "0.0";

  const stats = overall
    ? OVERALL_STATS
    : MODE_STAT_ORDER.filter(
        (stat) =>
          modeStats.includes(stat) &&
          player[stat] !== undefined &&
          clean(player[stat]) >= 0
      );

  return (
    <div className="group rounded-2xl border border-white/10 bg-black/25 p-4 hover:bg-white/[0.04] hover:border-[#FFBF00]/20 transition-all duration-200 ease-out">
      <div className="flex items-center gap-3">
        <RankBadge rank={player.rank} />

        <PlayerAvatar player={player} />

        <div className="min-w-0 flex-1">
          <div className="truncate font-black text-sm text-white">
            {player.name}
          </div>

          <div className="mt-1 text-xs uppercase tracking-widest text-white/30">
            {played > 0
              ? `${number(played)} games played`
              : "No games played"}
          </div>
        </div>

        <div className="text-right shrink-0">
          <div className="font-black text-lg text-[#FFBF00]">
            {number(wins)}
          </div>

          <div className="text-[8px] uppercase tracking-widest text-white/30">
            Wins
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
        {stats.map((stat) => (
          <Stat
            key={stat}
            stat={stat}
            value={player[stat]}
            compact
          />
        ))}

        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-white/35">Win Rate</span>
          <span className="font-bold text-emerald-400">
            {winRate}%
          </span>
        </div>
      </div>
    </div>
  );
}

export default function PlayZoneLeaderboard() {
  const [overall, setOverall] = useState([]);
  const [modes, setModes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedMode, setSelectedMode] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const [leaderboard, activity] = await Promise.all([
        fetchPlayZoneLeaderboards(),
        fetchPlayZoneActivity(),
      ]);

      setOverall(
        Array.isArray(leaderboard?.overall)
          ? leaderboard.overall
          : []
      );

      setModes(
        Array.isArray(leaderboard?.modes)
          ? leaderboard.modes
          : []
      );

      setActivities(
        Array.isArray(activity?.activities)
          ? activity.activities
          : []
      );
    } catch (error) {
      console.error("PlayZone load failed:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();

    const timer = setInterval(() => {
      load(true);
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  const selected = useMemo(() => {
    if (selectedMode === "ALL") return null;

    return (
      modes.find((mode) => mode.mode === selectedMode) || null
    );
  }, [modes, selectedMode]);

  if (loading) {
    return (
      <section className="relative py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-3xl glass p-8 animate-pulse">
            <div className="h-5 w-48 bg-white/10 rounded mb-4" />
            <div className="h-4 w-72 bg-white/5 rounded" />
          </div>
        </div>
      </section>
    );
  }

  const players =
    selectedMode === "ALL"
      ? overall.slice(0, 3)
      : (selected?.players || []);

  return (
    <section
      className="relative py-20 px-4 sm:px-6"
      data-testid="playzone-leaderboard-section"
    >
      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FFBF00]">
              <Gamepad2 size={16} />
              PlayZone
            </div>

            <h2 className="font-hero text-3xl sm:text-4xl mt-2">
              MiniGames Leaderboards
            </h2>

            <p className="text-sm text-white/45 mt-2">
              Top 3 players based on real PlayZone performance.
            </p>
          </div>

          <button
            type="button"
            onClick={() => load(true)}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-4 py-2 text-xs uppercase tracking-widest text-white/60 hover:border-[#FFBF00]/40 hover:text-white transition-all duration-200 ease-out"
          >
            <RefreshCw
              size={13}
              className={refreshing ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">

          {/* LEADERBOARD */}
          <div className="lg:col-span-2 rounded-3xl glass p-5 sm:p-6">

            {/* MODE SELECTOR */}
            <div className="mb-5 flex gap-2 overflow-x-auto pb-2">
              <button
                type="button"
                onClick={() => setSelectedMode("ALL")}
                className={`shrink-0 rounded-full px-4 py-2 text-xs uppercase tracking-widest transition ${
                  selectedMode === "ALL"
                    ? "bg-[#FFBF00] text-black font-bold"
                    : "border border-white/10 bg-black/20 text-white/50 hover:text-white"
                }`}
              >
                Overall
              </button>

              {(Array.isArray(modes) ? modes : []).map((mode) => (
                <button
                  key={mode.mode}
                  type="button"
                  onClick={() => setSelectedMode(mode.mode)}
                  className={`shrink-0 rounded-full px-4 py-2 text-xs uppercase tracking-widest transition ${
                    selectedMode === mode.mode
                      ? "bg-[#FFBF00] text-black font-bold"
                      : "border border-white/10 bg-black/20 text-white/50 hover:text-white"
                  }`}
                >
                  {mode.icon} {mode.label}
                </button>
              ))}
            </div>

            {/* TITLE */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="text-xl">
                  {selectedMode === "ALL"
                    ? "🏆"
                    : selected?.icon}
                </div>

                <div>
                  <div className="font-black text-white">
                    {selectedMode === "ALL"
                      ? "Overall Performance"
                      : selected?.label}
                  </div>

                  <div className="text-xs uppercase tracking-widest text-white/30">
                    {selectedMode === "ALL"
                      ? "Across all PlayZone modes"
                      : "Top 3 players"}
                  </div>
                </div>
              </div>

              <Trophy
                size={17}
                className="text-[#FFBF00]"
              />
            </div>

            {/* ONLY 3 PLAYERS */}
            <div
              className={
                selectedMode === "ALL"
                  ? "space-y-3"
                  : "space-y-3 max-h-[360px] overflow-y-auto overscroll-contain pr-2 scrollbar-thin scrollbar-thumb-white/15 scrollbar-track-transparent [scrollbar-width:thin]"
              }
            >
              {players.length > 0 ? (
                (Array.isArray(players)
                  ? selectedMode === "ALL"
                    ? players.slice(0, 3)
                    : players
                  : []
                ).map((player) => (
                  <PlayerCard
                    key={`${selectedMode}-${player.uuid || player.name}`}
                    player={player}
                    overall={selectedMode === "ALL"}
                    modeStats={
                      selectedMode === "ALL"
                        ? OVERALL_STATS
                        : selected?.stats || []
                    }
                  />
                ))
              ) : (
                <div className="rounded-2xl border border-white/5 bg-black/20 p-8 text-center">
                  <div className="text-3xl mb-3">🎮</div>
                  <div className="text-sm font-bold text-white/70">
                    No PlayZone data available
                  </div>
                  <div className="text-xs text-white/35 mt-1">
                    Players will appear here when statistics are available.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RECENT ACTIVITY */}
          <div className="rounded-3xl glass p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FFBF00]">
                  <Activity size={15} />
                  Live Activity
                </div>

                <h3 className="font-hero text-2xl mt-1">
                  Recent Activity
                </h3>
              </div>

              <div className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,.8)]" />
            </div>

            {activities.length > 0 ? (
              <div className="space-y-2">
                {(Array.isArray(activities) ? activities : [])
                  .slice(0, 8)
                  .map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl border border-white/5 bg-black/25 p-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-[#FFBF00]/10 grid place-items-center">
                        🎮
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white">
                          <span className="font-bold">
                            {item.player_name}
                          </span>{" "}
                          <span className="text-white/60">
                            {item.message ||
                              `${item.action} ${item.mode}`}
                          </span>
                        </div>

                        <div className="mt-1 flex items-center gap-2 text-xs uppercase tracking-widest text-white/30">
                          <span>{item.mode}</span>
                          <span>•</span>
                          <span>{timeAgo(item.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-white/5 bg-black/20 p-8 text-center">
                <div className="text-3xl mb-3">⚡</div>

                <div className="text-sm font-bold text-white/70">
                  No recent matches yet
                </div>

                <div className="text-xs text-white/35 mt-1">
                  PlayZone match activity will appear here.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
